import { Module } from 'vuex';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import router from '@/router';
import moment from 'moment';
import { serializer } from '../../api/shared/serializer';
import { MaintenanceType } from '@/models/MaintenanceType';
import { NewAdditionalService } from '@/models/additional-service/NewAdditionalService';
import {EventsTypes} from "@/enums/EventsTypes";
import {MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";
import Vue from "vue";

const maintenanceModule: Module<any, any> = {
  namespaced: false,
  state: {
    maintenanceRequests: [],
    currentMaintenanceRequest: null,
    currentResidentMaintenanceRequests: [],
    maintenanceRequestAvgCompletion: [],
    maintenanceCategoryValues: [],
    maintenanceTypes: [],
    closedMaintenanceRequests: [],
    scheduledMaintenance: [],
    currentSchedMaint: {},
    currentSchedMaintList: [],
    allClosedMaintenanceRequests: [],
    maintenanceCategories: []
  },

  mutations: {
    setMaintenanceRequests(state: any, payload: []) {
      state.maintenanceRequests = payload;
    },
    setMaintenanceRequestAvgCompletion(state: any, payload: []) {
      state.maintenanceRequestAvgCompletion = payload;
    },
    setCurrentMaintenanceRequest(state: any, payload: []) {
      state.currentMaintenanceRequest = payload;
    },
    setMaintenanceRequestsCurrentResident(state: any, payload: []) {
      state.currentResidentMaintenanceRequests = payload;
    },
    setMaintenanceCategoriyValues(state: any, payload: []) {
      state.maintenanceCategoryValues = payload;
    },
    setMaintenanceTypes(state: any, payload: MaintenanceType[]) {
      state.maintenanceTypes = payload;
    },
    setMaintenanceRequestsClosed(state: any, payload: MaintenanceType[]) {
      state.closedMaintenanceRequests = payload;
    },
    setScheduledMaintenance(state: any, payload: []) {
      state.scheduledMaintenance = payload;
    },
    setCurrentScheduledMaintenance(state: any, payload: []) {
      state.currentSchedMaint = payload;
    },
    setMaintenanceRequestsForSched(state: any, payload: []) {
      state.currentSchedMaintList = payload;
    },
    setAllMaintenanceRequestsClosed(state: any, payload: MaintenanceType[]) {
      state.allClosedMaintenanceRequests = payload;
    },
    setMaintenanceCategories(state, payload: []) {
      state.maintenanceCategories = payload;
    }
  },

  actions: {

    async getScheduledMaintenance({ commit }) {
      const facilityId = this.state.authModule.currentFacility;
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceScheduled')
        .where('facilityId', '==', facilityId);

      return await maintenanceRequests.get().then(async querySnapshot => {
        const maintReqs = [];
        const now = new Date();
        const docs = querySnapshot.docs;

        for (let i = 0; i < docs.length; i++) {
          const req = docs[i];
          const mntReg = req.data();
          mntReg.id = req.id;
          mntReg.maintainedBy = mntReg.maintainedBy || mntReg.vendor || mntReg.assignee;

          const isRequestsCompleted = await firebase
            .firestore()
            .collection("maintenanceRequests")
            .where("facilityId", "==", facilityId)
            .where("scheduledMaintenanceId", "==", mntReg.id)
            .orderBy("dateTime", "desc")
            .limit(5)
            .get()
            .then(async (querySnapshot) => {
              const maintenanceRequests = serializer.querySnapshotToDocuments(querySnapshot);
              if(maintenanceRequests && maintenanceRequests.length) {
                return maintenanceRequests.every((x: any) =>
                  (x.status === MaintenanceRequestStatus.Complete && x.completedDateTime) ||
                  (x.status === MaintenanceRequestStatus.Cancelled) ||
                  ([MaintenanceRequestStatus.InProgress, MaintenanceRequestStatus.New].includes(x.status) && moment().format('YYYY-MM-DD') <= moment(x.estimatedCompletionDate).format('YYYY-MM-DD'))
                );
              }

              return true;
            });

          mntReg.isRequestsCompleted = isRequestsCompleted;

          maintReqs.push(mntReg);
        }

        commit('setScheduledMaintenance', maintReqs);
      });
    },

    async getScheduledMaintenanceById({ commit }, id) {
      const snap = await firebase
        .firestore()
        .collection('maintenanceScheduled')
        .doc(id)
        .get();
      commit(
        'setCurrentScheduledMaintenance',
        serializer.documentSnapshotToDocument(snap)
      );
    },
    
    async getRequestsByScheduledMaintenanceId({ commit }, id) {
      const facilityId = this.state.authModule.currentFacility;
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', facilityId)
        .where('scheduledMaintenanceId', '==' , id)
        .orderBy('dateTime');

      return await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        const now = new Date() as any;
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          const timestamp = new Date(mntReg.dateTime.seconds * 1000) as any;
          const minutesDiff = Math.floor((now - timestamp) / (1000 * 60));

          mntReg.id = req.id;
          mntReg.dateTimeD = moment(mntReg.dateTime.seconds * 1000).toDate();
          mntReg.completionDateD = mntReg?.completedDateTime ? moment(mntReg.completedDateTime.seconds * 1000).toDate() : null;
          mntReg.requestAge = minutesDiff;
          maintReqs.push(mntReg);
        });
        commit('setMaintenanceRequestsForSched', maintReqs);
      });
    },

    async createScheduledMaintenance({ dispatch }, newSched) {
      newSched.createdDateTime =  firebase.firestore.Timestamp.now();
      newSched.lastCompleted = null;

      await firebase
        .firestore()
        .collection('maintenanceScheduled')
        .add(newSched)
        .then((data) => {

          if(newSched.startDate === moment(new Date()).format("YYYY-MM-DD")) {
            const facilityId = this.state.authModule.currentFacility;
            const newMaintenanceRequest: any = {};

            newMaintenanceRequest.status = (newSched.assigneeId && newSched.assigneeId?.length) ? MaintenanceRequestStatus.InProgress : MaintenanceRequestStatus.New;
            newMaintenanceRequest.dateTime = firebase.firestore.Timestamp.now();
            newMaintenanceRequest.scheduledMaintenanceId = data.id;
            newMaintenanceRequest.facilityId = facilityId;
            newMaintenanceRequest.locationId = newSched.locationId ?? null;
            newMaintenanceRequest.location = newSched.location ?? null;
            newMaintenanceRequest.description = newSched.description ?? null;
            newMaintenanceRequest.assigneeEmail = newSched.assigneeEmail ?? null;
            newMaintenanceRequest.assigneeId = newSched.assigneeId ?? null;
            newMaintenanceRequest.assignee = newSched.assignee ?? null;
            newMaintenanceRequest.assignedDate = newSched.assigneeId ? firebase.firestore.Timestamp.now() : null;
            newMaintenanceRequest.name = newSched.title;
            newMaintenanceRequest.icon = newSched.icon ?? 'home-clock';
            newMaintenanceRequest.typeId = newSched.typeId ?? null;
            newMaintenanceRequest.department = newSched.department ?? null;
            newMaintenanceRequest.createdByStaffId = newSched.createdByStaffId ?? null;
            newMaintenanceRequest.createdByStaffName = newSched.createdByStaffName ?? null;
            newMaintenanceRequest.categoryId = newSched.categoryId;
            newMaintenanceRequest.category = newSched.category;
            newMaintenanceRequest.wingId = newSched.wingId ?? null;
            newMaintenanceRequest.comment = newSched.comment ?? null;
            newMaintenanceRequest.priority = newSched.priority ?? null;
            newMaintenanceRequest.isSendEmailToVendor = newSched.isSendEmailToVendor ?? null;
            newMaintenanceRequest.estimatedCompletionDate = moment(new Date()).add(24, 'hours').format("YYYY-MM-DD");

            dispatch("createMaintenanceRequest", newMaintenanceRequest);
          }

          dispatch("getMaintenanceRequests")
          dispatch("getScheduledMaintenance")
          return data;
        });        

    },

    async updateScheduledMaintenance({ dispatch }, scheduled) {
      const scheduledMaintenanceId = scheduled.id;
      const scheduledMaintenance = Object.assign({}, scheduled);
      delete scheduledMaintenance.id;

      await firebase
        .firestore()
        .collection('maintenanceScheduled')
        .doc(scheduledMaintenanceId)
        .update(scheduledMaintenance)
        .then(async (data) => {
          await firebase
            .firestore()
            .collection('maintenanceRequests')
            .where("facilityId", "==", this.state.authModule.currentFacility)
            .where("scheduledMaintenanceId", "==", scheduledMaintenanceId)
            .get()
            .then( async (querySnapshot) => {
              const maintenanceRequests = querySnapshot.docs.map(x => ({id: x.id, ref: x.ref, data: x.data()}));
              for(const maintenanceRequest of maintenanceRequests) {
                const updates: any = {
                  category: scheduledMaintenance.category,
                  categoryId: scheduledMaintenance.categoryId,
                  description: scheduledMaintenance.description ?? null,
                  name: scheduledMaintenance.title,
                  locationId: scheduledMaintenance.locationId ?? null,
                  location: scheduledMaintenance.location ?? null,
                  assignee: scheduledMaintenance.assignee ?? null,
                  assigneeId: scheduledMaintenance.assigneeId ?? null,
                  assigneeEmail: scheduledMaintenance.assigneeEmail ?? null,
                  assignedDate: scheduledMaintenance.assigneeId ? firebase.firestore.Timestamp.now() : null,
                  wingId: scheduledMaintenance.wingId ?? null,
                  comment: scheduledMaintenance.comment ?? null,
                  priority: scheduledMaintenance.priority ?? null,
                  isSendEmailToVendor: scheduledMaintenance.isSendEmailToVendor ?? null,
                  icon: scheduledMaintenance.icon ?? 'home-clock',
                  typeId: scheduledMaintenance.typeId ?? null,
                  department: scheduledMaintenance.department ?? null
                }

                await maintenanceRequest.ref.update(updates);
              }

              dispatch("getMaintenanceRequests")
              dispatch("getScheduledMaintenance")
            })
        })
    },

    async deleteScheduledMaintenance({ dispatch }, scheduledId) {
      await firebase
        .firestore()
        .collection('maintenanceScheduled')
        .doc(scheduledId)
        .delete()
        .then(async (data) => {
          await firebase
            .firestore()
            .collection('maintenanceRequests')
            .where("facilityId", "==", this.state.authModule.currentFacility)
            .where("scheduledMaintenanceId", "==", scheduledId)
            .get()
            .then( async (querySnapshot) => {
              const maintenanceRequests = querySnapshot.docs.map(x => ({id: x.id, ref: x.ref, data: x.data()}));
              for(const maintenanceRequest of maintenanceRequests) {
                await maintenanceRequest.ref.delete();
              }

              dispatch("getMaintenanceRequests")
              dispatch("getScheduledMaintenance")
            })
        })
    },
    
    async createMaintenanceRequest({ dispatch }, newMaintenanceRequest) {

      newMaintenanceRequest.dateTime =  firebase.firestore.Timestamp.now();
      firebase
        .firestore()
        .collection('maintenanceRequests')
        .add(newMaintenanceRequest)
        .then((data) => {
          dispatch("getMaintenanceRequests")
          return data;
        });        

    },

    async getMaintenanceRequests({ commit }) {
      const facilityId = this.state.authModule.currentFacility;
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', facilityId)
        .where('status', 'in' , ['new', 'inprogress'])
        .orderBy('dateTime');
      return await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        const now = new Date() as any;
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          const timestamp = new Date(mntReg.dateTime.seconds * 1000) as any;
          const minutesDiff = Math.floor((now - timestamp) / (1000 * 60));
          mntReg.id = req.id;
          mntReg.requestAge = minutesDiff;
          maintReqs.push(mntReg);
        });
        commit('setMaintenanceRequests', maintReqs);
      });
    },

    async getMaintenanceRequest({ commit }, id) {
      const snap = await firebase
        .firestore()
        .collection('maintenanceRequests')
        .doc(id)
        .get();
      commit(
        'setCurrentMaintenanceRequest',
        serializer.documentSnapshotToDocument(snap)
      );
    },

    async editMaintenanceRequest({ dispatch }, request) {
      await firebase
        .firestore()
        .collection("maintenanceRequests")
        .doc(request.id)
        .update(request.updates)
        .then(() => {
          dispatch("getMaintenanceRequest", request.id)
        })
    },

    async getClosedMaintenanceRequests({ commit }, { startDate, endDate }) {
      const maintenanceRequestsRef = firebase.firestore().collection('maintenanceRequests');

      const [closedRequestsSnapshot, cancelledRequestsSnapshot] = await Promise.all([
        maintenanceRequestsRef
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('status', '==', MaintenanceRequestStatus.Complete)
          .where('completedDateTime', '>', startDate)
          .where('completedDateTime', '<', endDate)
          .get(),

        maintenanceRequestsRef
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('status', '==', MaintenanceRequestStatus.Cancelled)
          .where('cancelledDateTime', '>', startDate)
          .where('cancelledDateTime', '<', endDate)
          .get()
      ]);

      const combinedRequests = [];

      closedRequestsSnapshot.docs.forEach(req => {
        const mntReg = req.data();
        const completedDateTime = moment(mntReg.completedDateTime.seconds * 1000);
        const openDuration = moment
          .duration(completedDateTime.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
          .asDays();
        mntReg.id = req.id;
        mntReg.openDuration = Math.round(openDuration);
        combinedRequests.push(mntReg);
      });

      cancelledRequestsSnapshot.docs.forEach(req => {
        const mntReg = req.data();
        const cancelledDateTime = moment(mntReg.cancelledDateTime.seconds * 1000);
        const openDuration = moment
          .duration(cancelledDateTime.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
          .asDays();
        mntReg.id = req.id;
        mntReg.openDuration = Math.round(openDuration);
        combinedRequests.push(mntReg);
      });

      commit('setMaintenanceRequestsClosed', combinedRequests);
    },

    async getAllClosedMaintenanceRequests({ commit }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('status', '==', 'complete')

      await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          if (mntReg.completedDateTime?.seconds) {
            const completedDateTime = moment(mntReg.completedDateTime.seconds * 1000);
            const openDuration = moment
              .duration(completedDateTime.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
              .asDays();
            mntReg.openDuration = Math.round(openDuration);
          }
          mntReg.id = req.id;
          maintReqs.push(mntReg);
        });
        commit('setAllMaintenanceRequestsClosed', maintReqs);
      })
    },

    async getMainRequestsByResident({ commit }, { residentId }) {
      const now = moment(new Date());
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('status', '==', 'new')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('residentId', '==', residentId)
        .orderBy('dateTime');
      await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          const reqDateTime = moment
            .duration(now.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
            .asMinutes();
          mntReg.id = req.id;
          mntReg.requestAge = reqDateTime;
          maintReqs.push(mntReg);
        });
        commit('setMaintenanceRequestsCurrentResident', maintReqs);
      });
    },

    async getMaintenanceRequestAvgCompletion({ commit }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('status', '==', 'complete');
      await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          const now = moment(mntReg.completedDateTime.seconds * 1000);
          const completionTime = moment
            .duration(now.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
            .asMinutes();
          mntReg.id = req.id;
          mntReg.completionTime = completionTime;
          maintReqs.push(mntReg);
        });
        commit('setMaintenanceRequestAvgCompletion', maintReqs);
      });
    },

    async getMaintenanceCategoryValues({ commit }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', this.state.authModule.currentFacility);

      await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        let lightbulb = 0;
        let plumbing = 0;
        let cleaning = 0;
        let tvtablet = 0;
        let bathroom = 0;
        let batteries = 0;
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          if (mntReg.category == 'lightbulb') {
            lightbulb++;
          } else if (mntReg.category == 'plumbing') {
            plumbing++;
          } else if (mntReg.category == 'cleaning') {
            cleaning++;
          } else if (mntReg.category == 'TVTablet') {
            tvtablet++;
          } else if (mntReg.category == 'bathroom') {
            bathroom++;
          } else if (mntReg.category == 'batteries') {
            batteries++;
          }
        });
        maintReqs.push(
          plumbing,
          cleaning,
          lightbulb,
          tvtablet,
          bathroom,
          batteries
        );
        commit('setMaintenanceCategoriyValues', maintReqs);
      });
    },

    async completeMaintenanceRequest({ commit }, { id, additionalResponse }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .doc(id);
      const completedDateTime = firebase.firestore.Timestamp.now();
      await maintenanceRequests.update({
        status: 'complete',
        additionalResponse: additionalResponse,
        completedDateTime: completedDateTime,
      });

      router.push('/maintenanceRequests');
    },

    async assignMaintenanceRequest({ commit }, { id, assignee, assigneeId, assigneeEmail, estimatedCompletionDate, priority, isSendEmailToVendor }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .doc(id);
      const today = firebase.firestore.Timestamp.now();
      await maintenanceRequests.update({
        status: 'inprogress',
        assignedDate: today,
        assignee: assignee,
        assigneeId: assigneeId,
        assigneeEmail: assigneeEmail,
        estimatedCompletionDate: estimatedCompletionDate,
        priority: priority,
        isSendEmailToVendor: isSendEmailToVendor
      });

      router.push(`/maintenanceRequests?tab=2`);
    },

    async getMaintenanceTypes({ commit }) {
      await firebase
        .firestore()
        .collection('maintenanceRequestTypes')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .get()
        .then(querySnapshots => {
          const maintenanceTypes: MaintenanceType[] = serializer.querySnapshotToDocuments(
            querySnapshots
          );
          commit('setMaintenanceTypes', maintenanceTypes);
        });
    },

    async createMaintenanceType(
      { dispatch },
      maintenanceType: MaintenanceType
    ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequestTypes')
          .add(maintenanceType)
          .then(() => {
            dispatch('getMaintenanceTypes');
          });
      } catch (error) {
        return error;
      }
    },

    async updateMaintenanceType(
      { dispatch },
      maintenanceType: MaintenanceType
    ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequestTypes')
          .doc(maintenanceType.id)
          .update({
            name: maintenanceType.name,
            category: maintenanceType.category,
            categoryId: maintenanceType.categoryId,
            icon: maintenanceType.icon,
            department: maintenanceType.department,
          })
          .then(() => {
            dispatch('getMaintenanceTypes');
            dispatch('updateMaintenanceRequestsByType', maintenanceType);
          });
      } catch (error) {
        return error;
      }
    },

    async updateMaintenanceRequestsByType(_, maintenanceType: MaintenanceType) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequests')
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('typeId', '==', maintenanceType.id)
          .get()
          .then(querySnapshot => {
            if (querySnapshot.docs && querySnapshot.docs.length) {
              querySnapshot.docs.forEach(doc => {
                doc.ref.update({
                  name: maintenanceType.name,
                  category: maintenanceType.category,
                  icon: maintenanceType.icon,
                  department: maintenanceType.department,
                });
              });
            }
          });
      } catch (error) {
        return error;
      }
    },

    async updateMaintenanceRequestsByResident(_, resident) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequests')
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('residentId', '==', resident.id)
          .where('status', '==', 'new')
          .get()
          .then(querySnapshot => {
            if (querySnapshot.docs && querySnapshot.docs.length) {
              const updates: any = {};
              const roomName = resident?.roomName || '';
              const address = resident?.address || '';

              if (roomName || address) {
                updates.location = `${roomName} ${address}`;
              }

              if (resident?.hasOwnProperty('name') && resident?.name) {
                updates.residentName = resident.name;
              }

              if (resident?.hasOwnProperty('phone') && resident?.phone) {
                updates.phone = resident?.phone;
              }

              if (Object.keys(updates).length) {
                querySnapshot.docs.forEach(doc => {
                  doc.ref.update(updates);
                });
              }
            }
          });
      } catch (error) {
        return error;
      }
    },

    async deleteMaintenanceType(
      { dispatch },
      maintenanceType: MaintenanceType
    ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequestTypes')
          .doc(maintenanceType.id)
          .delete()
          .then(() => {
            dispatch('getMaintenanceTypes');
          });
      } catch (error) {
        return error;
      }
    },

    async getMaintenanceCategories({ commit }) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceCategories')
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .get()
          .then((querySnapshot) => {
            const maintenanceCategories = serializer.querySnapshotToDocuments(querySnapshot);
            commit('setMaintenanceCategories', maintenanceCategories)
          })
      } catch (err) {
        return err;
      }
    },

    async createMaintenanceCategory({ dispatch }, maintenanceCategory) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceCategories')
          .add(maintenanceCategory)
          .then(() => {
            dispatch("getMaintenanceCategories")
          })
      } catch (err) {
        return err;
      }
    },

    async updateMaintenanceCategory({ dispatch }, maintenanceCategory) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceCategories')
          .doc(maintenanceCategory.id)
          .update({
            name: maintenanceCategory.name
          })
          .then(() => {
            dispatch("getMaintenanceCategories")
          })
      } catch (err) {
        return err;
      }
    },

    async updateMaintenanceCategoryForSelectedCollection(_, data) {
      try {
        await firebase
          .firestore()
          .collection(data.collection)
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where("categoryId", "==", data.maintenanceCategory.id)
          .get()
          .then(async (querySnapshot) => {
            const vendors = querySnapshot.docs.map(x => ({ id: x.id, ref: x.ref, data: x.data() }))

            for(const vendor of vendors) {
              await vendor.ref.update({
                category: data.maintenanceCategory.name
              })
            }
          })
      } catch (err) {
        return err;
      }
    },

    async deleteMaintenanceCategory({ dispatch }, maintenanceCategory) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceCategories')
          .doc(maintenanceCategory.id)
          .delete()
          .then(() => {
            dispatch("getMaintenanceCategories")
          })
      } catch (err) {
        return err;
      }
    },

    async getMaintenanceReport({ dispatch }, { startDate, endDate }) {
      const report = {
        requests: null,
        assignedRequests: null
      }

      report.requests = await firebase
        .firestore()
        .collection("maintenanceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("dateTime", ">", startDate)
        .where("dateTime", "<", endDate)
        .get()
        .then((querySnapshot) => {
          const maintenanceRequests: Array<any> = [];

          querySnapshot.docs.forEach((x) => {
            const request = x.data();
            request.id = x.id;

            if(request.status === MaintenanceRequestStatus.New) {
              request.color = "primary--text";
            } else if(request.status === MaintenanceRequestStatus.InProgress) {
              request.color = "orange--text";
            } else if(request.status === MaintenanceRequestStatus.Complete) {
              request.color = "green--text";
            } else if(request.status === MaintenanceRequestStatus.Cancelled) {
              request.color = "red--text";
            }

            maintenanceRequests.push(request);
          });

          return maintenanceRequests;
        })

      const totalRequests = report.requests.filter(x => x?.assignee && x?.assignedDate).length;

      const assignedRequests = Object.entries(
        report.requests
          .filter(x => x?.assignee && x?.assignedDate)
          .reduce((acc, item) => {
            const assignee = item?.assigneeEmail ?? item?.assignee;
            acc[assignee] = (acc[assignee] || 0) + 1;
            return acc;
          }, {})
      ).map(([email, count]) => {
        const numericCount = Number(count);
        const numericTotalRequests = Number(totalRequests);

        if (isNaN(numericCount) || isNaN(numericTotalRequests)) {
          throw new Error("Invalid data: count or totalRequests is NaN");
        }

        const percent = numericTotalRequests === 0 ? "0.00" : ((numericCount / numericTotalRequests) * 100).toFixed(2);

        return {
          email: email,
          count: numericCount,
          percent: percent,
          name: ""
        };
      });

      for(const assignedRequest of assignedRequests) {
        let name = await firebase
         .firestore()
         .collection("vendors")
         .where("facilityId", "==", this.state.authModule.currentFacility)
         .where("email", "==", assignedRequest.email)
         .get()
         .then(async (querySnapshot) => {
           const maintenanceTechnicians: any = serializer.querySnapshotToDocuments(querySnapshot);
           return maintenanceTechnicians[0]?.name;
         })

        if(!name) {
          name = await firebase
           .firestore()
           .collection("users")
           .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
           .where("email", "==", assignedRequest.email)
           .get()
           .then(async (querySnapshot) => {
             const users: any = serializer.querySnapshotToDocuments(querySnapshot);
             return users[0]?.name;
           })
         }

        assignedRequest.name = name;
      }

      report.assignedRequests = assignedRequests;

      return report;
    },
  },
  getters: {
    maintenanceCategories(state) {
      return [{ id: "All", name: "All" }, ...state.maintenanceCategories];
    }
  }
};

export default maintenanceModule;
